import type { GachaEvmPurchase } from "@anaconda/core/gacha/evm-purchase"
import { action, atom } from "@reatom/core"
import { createGachaEvmDeliveryTracking } from "./gacha-evm-delivery-tracking"
import { requestGachaPortfolioRefresh } from "./gacha-portfolio-refresh"

export function isGachaEvmLateBroadcast(
  previous: GachaEvmPurchase | undefined,
  record: GachaEvmPurchase,
) {
  return (
    previous?.state === "payment_rejected" &&
    Boolean(record.reportedHash) &&
    ["payment_pending", "paid", "fulfilling"].includes(record.state)
  )
}

export function isGachaEvmPurchaseBlocking(record: GachaEvmPurchase) {
  // Failed or unverified payments recover independently of a new purchase.
  // Clean reservations have no payment permission.
  return (
    record.active &&
    !["payment_failed", "payment_pending"].includes(record.state) &&
    !(
      ["reserving", "reserved"].includes(record.state) &&
      !record.reportedHash &&
      !record.evidence
    )
  )
}

const records = atom<GachaEvmPurchase[]>([], "gacha.evm.purchases.records")
const upsert = action((record: GachaEvmPurchase) => {
  const previous = records().find((item) => item.id === record.id)
  // HTTP responses may arrive out of order after a worker or another caller
  // settled the pack. Never resurrect an observed terminal record locally.
  if (
    (previous &&
      !previous.active &&
      record.active &&
      !isGachaEvmLateBroadcast(previous, record)) ||
    (previous?.reportedHash && !record.reportedHash) ||
    (previous?.state === "delivered" && record.state !== "delivered") ||
    (previous?.evidence && !record.evidence) ||
    (previous?.state === "payment_pending" &&
      ["reserving", "reserved"].includes(record.state))
  )
    return
  records.set((current) => [
    record,
    ...current.filter(
      (item) => item.id !== record.id && item.userId === record.userId,
    ),
  ])
}, "gacha.evm.purchases.upsert")
const signingOwner = atom<string | null>(
  null,
  "gacha.evm.purchases.signingOwner",
)
const deliveryTracking = createGachaEvmDeliveryTracking(
  records,
  "gacha.evm.purchases.deliveryTracking",
)
export const gachaEvmPurchases = {
  records,
  upsert,
  signingOwner,
  deliveryTracking,
}

// Ticket 06 may subscribe without changing purchase orchestration. Identity is
// chain + collection + original wallet; a token ID alone is never sufficient.
export const GACHA_EVM_OUTCOME_REFRESH_EVENT = "gacha:evm-outcome-refresh"
export type GachaEvmOutcomeRefresh = {
  state: GachaEvmPurchase["state"]
  chainId: number
  cardContract: string
  walletAddress: string
  memo?: string
  tokenId?: string
}
export function requestGachaEvmOutcomeRefresh(record: GachaEvmPurchase) {
  if (typeof window === "undefined") return
  if (record.state === "delivered") requestGachaPortfolioRefresh()
  const detail: GachaEvmOutcomeRefresh = {
    state: record.state,
    chainId: record.chain.chainId,
    cardContract: record.chain.cardContract,
    walletAddress: record.walletAddress,
    memo: record.memo,
    tokenId: record.award?.evm_token_id ?? undefined,
  }
  window.dispatchEvent(
    new CustomEvent<GachaEvmOutcomeRefresh>(GACHA_EVM_OUTCOME_REFRESH_EVENT, {
      detail,
    }),
  )
}
